import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import { deriveAddressFormValues } from '../../utils/profileFormValues';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './CompleteProfilePage.module.css';

const CompleteProfilePage = () => {
  const { user, refreshUser, ineligibleFields } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'creating'

  const [formData, setFormData] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    dateOfBirth: user?.profile?.dateOfBirth?.split('T')[0] || '',
    phone: user?.profile?.phone || '',
    gender: user?.profile?.gender || '',
    weight: user?.profile?.weight || '',
    heightFeet: '',
    heightInches: '',
    ...deriveAddressFormValues(user)
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = ({ street, city, state, zipCode }) => {
    setFormData(prev => ({
      ...prev,
      address: street || prev.address,
      city: city || prev.city,
      state: state || prev.state,
      zip: zipCode || prev.zip
    }));
  };

  const isFormValid = () => {
    return formData.firstName && formData.lastName && formData.dateOfBirth &&
           formData.phone && formData.gender && formData.weight &&
           formData.heightFeet && formData.heightInches !== '' &&
           formData.address && formData.state && formData.city && formData.zip;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    setStep('creating');

    // Safety timeout: if stuck on spinner for 30s, go back to form
    const safetyTimeout = setTimeout(() => {
      setSaving(false);
      setStep('form');
      toast('Request timed out. Please try again.', 'error');
    }, 30000);

    try {
      const heightCombined = `${formData.heightFeet}'${formData.heightInches}"`;

      const profilePayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        phone: formData.phone,
        gender: formData.gender,
        weight: formData.weight,
        height: heightCombined,
        address: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zip
        }
      };

      const response = await api.post('/users/complete-profile', profilePayload);

      // API returned 200 = profile is saved. Redirect immediately.
      clearTimeout(safetyTimeout);
      try { toast('Profile complete! Welcome to Oval Care.', 'success'); } catch (_) { /* toast optional */ }
      window.location.href = '/dashboard';
      return;
    } catch (error) {
      clearTimeout(safetyTimeout);
      console.error('[CompleteProfile] Error:', error);
      // Custom api client throws Error with .message, .status, .errors (NOT .response.data)
      let msg = 'Failed to save profile. Please try again.';
      if (error.message && error.message !== 'Request failed') {
        msg = error.message;
      } else if (error.response?.data?.error) {
        msg = error.response.data.error;
      } else if (error.errors?.length) {
        msg = error.errors.map(e => e.message || e).join(', ');
      }
      toast(msg, 'error');
      setStep('form');
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img src="/oval-logo.svg" alt="Oval Care" className={styles.logo} onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 className={styles.title}>Complete Your Profile</h1>
          <p className={styles.subtitle}>
            We need a few details to set up your care account. Please ensure your address is accurate — it's used for prescriptions and shipping.
          </p>
        </div>

        {(ineligibleFields ?? []).includes('dateOfBirth') && (
          <div className={styles.ineligibleAlert} role="alert">
            The date of birth on your account does not meet our minimum age requirement, so your care
            account cannot be activated. Please correct it below, or contact support if this is a mistake.
          </div>
        )}

        {step === 'creating' ? (
          <div className={styles.creatingState}>
            <div className={styles.spinner} />
            <h2>Setting up your account...</h2>
            <p>We're verifying your information and creating your care profile. This usually takes a few seconds.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>First Name <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Last Name <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Date of Birth <span className={styles.required}>*</span></label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Phone <span className={styles.required}>*</span></label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Gender <span className={styles.required}>*</span></label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleChange('gender', e.target.value)}
                  className={styles.select}
                  required
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Weight (lbs) <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => handleChange('weight', e.target.value)}
                  placeholder="e.g., 180"
                  className={styles.input}
                  min="50"
                  max="800"
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Height <span className={styles.required}>*</span></label>
                <div className={styles.heightRow}>
                  <div className={styles.heightField}>
                    <select
                      value={formData.heightFeet}
                      onChange={(e) => handleChange('heightFeet', e.target.value)}
                      className={styles.select}
                      required
                    >
                      <option value="">Feet</option>
                      <option value="3">3 ft</option>
                      <option value="4">4 ft</option>
                      <option value="5">5 ft</option>
                      <option value="6">6 ft</option>
                      <option value="7">7 ft</option>
                    </select>
                  </div>
                  <div className={styles.heightField}>
                    <select
                      value={formData.heightInches}
                      onChange={(e) => handleChange('heightInches', e.target.value)}
                      className={styles.select}
                      required
                    >
                      <option value="">Inches</option>
                      <option value="0">0 in</option>
                      <option value="1">1 in</option>
                      <option value="2">2 in</option>
                      <option value="3">3 in</option>
                      <option value="4">4 in</option>
                      <option value="5">5 in</option>
                      <option value="6">6 in</option>
                      <option value="7">7 in</option>
                      <option value="8">8 in</option>
                      <option value="9">9 in</option>
                      <option value="10">10 in</option>
                      <option value="11">11 in</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.divider}>
              <span>Address</span>
            </div>

            <div className={styles.fieldFull}>
              <AddressAutocomplete
                value={formData.address}
                onChange={(val) => handleChange('address', val)}
                onSelect={handleAddressSelect}
                placeholder="Start typing your address..."
                label="Street Address"
              />
            </div>

            <div className={styles.row3}>
              <div className={styles.field}>
                <label className={styles.label}>State <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., FL"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>City <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., Miami"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>ZIP <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  placeholder="e.g., 33101"
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={saving || !isFormValid()}
            >
              {saving ? 'Saving...' : 'Complete Profile & Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CompleteProfilePage;
